import {Moment, MomentInput} from 'moment-timezone';
import moment from '~/services/moment';

export type DayOfWeekTitles = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
export type DayOfWeekAlias = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';

export interface DayOfWeeks {
  title: DayOfWeekTitles;
  alias: DayOfWeekAlias;
}

export interface DayOfWeekObject {
  monday: DayOfWeeks;
  tuesday: DayOfWeeks;
  wednesday: DayOfWeeks;
  thursday: DayOfWeeks;
  friday: DayOfWeeks;
  saturday: DayOfWeeks;
  sunday: DayOfWeeks;
}

export const DAY_OF_WEEKS: DayOfWeekObject = {
  monday: {
    title: 'Monday',
    alias: 'MON',
  },
  tuesday: {
    title: 'Tuesday',
    alias: 'TUE',
  },
  wednesday: {
    title: 'Wednesday',
    alias: 'WED',
  },
  thursday: {
    title: 'Thursday',
    alias: 'THU',
  },
  friday: {
    title: 'Friday',
    alias: 'FRI',
  },
  saturday: {
    title: 'Saturday',
    alias: 'SAT',
  },
  sunday: {
    title: 'Sunday',
    alias: 'SUN',
  },
};

export const DATE_FORMAT = 'YYYY-MM-DD';
export const LONG_DATE_FORMAT = 'DD MMM YYYY';
export const LONG_MONTH_FORMAT = 'MMMM DD, YYYY';
export const SHORT_DATE_TIME_FORMAT = 'DD MMM - LT';
export const LONG_DATE_TIME_FORMAT = 'DD MMM YYYY - LT';
export const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'LT';
export const LONG_MONTH_NAME_FORMAT = 'DD MMM YYYY';
export const DATE_TIME_24H_FORMAT = 'DD.MM.YYYY HH:mm';
export const APPOINTMENT_DATE_RANGE_FORMAT = 'DD.MM.YYYY';
export const CONSULTATION_DATE_TIME_FORMAT = 'DD.MM.YYYY [at] HH:mm A';
export const HOUR_MIN_24H_FORMAT = 'H:mm';
export const TIME_ZONE_FORMAT = 'HH:mm Z';
export const TIME_24H_FORMAT = 'HH:mm';
export const HOUR_24H_FORMAT = 'HH';
export const COMBINE_DATE_TIME_FORMAT = `${DATE_FORMAT}LT`;
export const YEAR_FORMAT = 'yyyy';
export const MESSAGE_HOVER_FORMAT = 'ddd, MMM DD YYYY, HH:mm';
export const DATE_OF_BIRTH_TIME_FORMAT = 'dd MMMM yyyy';
export const DATE_MEASUREMENT_FORMAT = 'dd   /   MMMM   /   yyyy';

export function formatYear(value: moment.MomentInput) {
  if (!value) {
    return '';
  }
  return moment(value).format(YEAR_FORMAT);
}

export function formatDate(value: moment.MomentInput) {
  if (!value) {
    return '';
  }
  return moment(value).format(DATE_FORMAT);
}

export function formatTime(value: moment.MomentInput) {
  if (!value) {
    return '';
  }
  return moment(value).format(TIME_FORMAT);
}

export function formatShortDateTime(value: moment.MomentInput) {
  if (!value) {
    return '';
  }
  return moment(value).format(SHORT_DATE_TIME_FORMAT);
}

export function formatLongDateTime(value: moment.MomentInput) {
  if (!value) {
    return '';
  }
  return moment(value).format(LONG_DATE_TIME_FORMAT);
}

export function formatLongMonth(value: moment.MomentInput) {
  if (!value) {
    return '';
  }
  return moment(value).format(SHORT_DATE_TIME_FORMAT);
}

export function formatDateTime24H(value: moment.MomentInput) {
  if (!value) {
    return '';
  }
  return moment(value).format(DATE_TIME_24H_FORMAT);
}

export function formatAppointmentDateRange(value: moment.MomentInput) {
  if (!value) {
    return '';
  }
  return moment(value).format(APPOINTMENT_DATE_RANGE_FORMAT);
}

export function formatIOString(time: moment.MomentInput) {
  let momentTime = time as Moment;
  if (!(time instanceof moment)) {
    momentTime = moment(time);
  }
  return momentTime.toISOString()?.replace(/[.]\d+/, '');
}

export function formatLocalStartOfDate(value?: moment.MomentInput) {
  let startOfDay = moment.utc(new Date()).clone().local().startOf('day');
  if (value) {
    startOfDay = moment.utc(value).clone().local().startOf('day');
  }
  return startOfDay.format();
}

export function formatLocalEndOfDate(value?: moment.MomentInput) {
  let endOfDay = moment.utc(new Date()).clone().local().endOf('day');
  if (value) {
    endOfDay = moment.utc(value).clone().local().endOf('day');
  }
  return endOfDay.format();
}

export function formatStartOfDate(value?: moment.MomentInput) {
  // 2022-02-02T00:00:00Z
  let startOfDay = moment.utc(new Date()).clone().startOf('day');
  if (value) {
    startOfDay = moment.utc(value).clone().startOf('day');
  }
  return formatIOString(startOfDay);
}

export function formatStartOfDateLocal(value?: moment.MomentInput) {
  let startOfDay = moment.utc(new Date()).clone().local().startOf('day');
  if (value) {
    startOfDay = moment.utc(value).clone().local().startOf('day');
  }
  return formatIOString(startOfDay);
}

export function formatEndOfDate(value?: moment.MomentInput) {
  // 2022-02-02T00:00:00Z
  let endOfDay = moment.utc(new Date()).clone().endOf('day');
  if (value) {
    endOfDay = moment.utc(value).clone().endOf('day');
  }
  return formatIOString(endOfDay);
}

export function combineDateTime(date: string, time: string, format: true): string;
export function combineDateTime(date: string, time: string, format: false): Moment;
export function combineDateTime(date: string, time: string, format?: boolean): string;
export function combineDateTime(date: string, time: string, format = true) {
  if (!date) {
    date = formatDate(new Date());
  }
  if (!time) {
    time = formatTime(new Date());
  }
  const momentObj = moment(date + time, COMBINE_DATE_TIME_FORMAT);
  return format ? momentObj.format() : momentObj; // 2022-02-02T23:30:00+07:00Z
}

export function convertTimeTo4Digit(dateTime?: string | Moment) {
  if (!dateTime) {
    return '';
  }
  // https://github.com/moment/moment/issues/1174#issuecomment-25950746
  const timeStr = moment(dateTime).format('HH:mm');
  return timeStr === '00:00' ? '24:00' : timeStr;
}

export type Weekdays = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export const weekdays: Weekdays[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export function getDatesInAWeek(date: MomentInput): Moment[];
export function getDatesInAWeek(date: MomentInput, format: string): string[];
export function getDatesInAWeek(date: MomentInput, format?: string): (Moment | string)[] {
  const firstDayOfWeek = moment(date).startOf('w');
  return weekdays.map((weekDay, idx) => {
    const dateMm = firstDayOfWeek.clone().add(idx, 'day');
    return format ? dateMm.format(format) : dateMm;
  });
}

const [mon, tue, wed, thu, fri, sat, sun] = getDatesInAWeek(moment()).map(v => v.format(DATE_FORMAT));
const [nextMon, nextTue, nextWed, nextThu, nextFri, nextSat, nextSun] = getDatesInAWeek(moment().add(1, 'w')).map(v =>
  v.format(DATE_FORMAT),
);
export const weekdayToDateMap = {mon, tue, wed, thu, fri, sat, sun};
export const nextWeekdayToDateMap = {
  mon: nextMon,
  tue: nextTue,
  wed: nextWed,
  thu: nextThu,
  fri: nextFri,
  sat: nextSat,
  sun: nextSun,
};

export const getWeekFromNow = (date: Moment) => {
  const currentWeek = moment().get('w');
  const selectedWeek = date.get('w');
  const diff = currentWeek - selectedWeek;
  if (diff === 0) {
    return 'This week';
  }
  if (diff === -1) {
    return 'Next week';
  }
  if (diff === 1) {
    return 'Last week';
  }
  if (diff < -1) {
    return `Next ${Math.abs(diff)} weeks`;
  }
  if (diff > 0) {
    return `Past ${diff} weeks`;
  }
};
